import type { Unsubscribe } from '@reduxjs/toolkit';
import h from 'hyperscript';

import pauseIcon from '@disy/cadenza-icons/pause.svg';
import playIcon from '@disy/cadenza-icons/play.svg';
import { formatDateTime, formatRelativeTime } from '@disy/lib/format';
import { showToastMessage } from '@disy/ui/toast-message';
import { getSetting } from '@disy/workbook.lib/settings';

import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore, unsubscribeStore } from 'cadenza/store/state-utils';
import { CircleProgressBarButton } from 'cadenza/workbook/header/live-mode-action/circle-progress-bar-button/circle-progress-bar-button';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import { toggleLiveMode } from 'cadenza/workbook/state/workbook-live-mode-actions.state';
import {
  getLiveModeLastRefreshTime,
  getLiveModeState,
  isLiveModeEnabled,
  isLiveModeRunning,
} from 'cadenza/workbook/state/workbook-live-mode-selectors.state';
import { closeTimelinePlayer } from 'cadenza/workbook/timelines/timeline.state';
import { getActiveWorksheet } from 'cadenza/workbook/workbook-core-selectors.state';

import './live-mode-action.css';
import i18n from './live-mode-action.properties';

const COMPONENT_NAME = 'd-live-mode-action';

export class LiveModeAction extends HTMLElement {
  readonly #lastUpdateTime = h('label.last-update-time');
  readonly #liveModeButton = h(`.${COMPONENT_NAME}--content`, {
    onclick() {
      toggleLiveMode();
      getStore().dispatch(closeTimelinePlayer());
    },
  });

  #unsubscribeFromStore: Unsubscribe[] = [];
  #updateTimer?: number;

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.append(this.#liveModeButton, this.#lastUpdateTime);

    this.#unsubscribeFromStore = [
      observeStore(getStore(), isLiveModeEnabled, (isEnabled) => {
        this.hidden = !isEnabled;
      }),
      observeStore(getStore(), getLiveModeState, (statusChange, currentState) => {
        const isStarting = statusChange?.state === 'running';

        // Update the button
        const content = this.querySelector(`.${COMPONENT_NAME}--content`)!;
        content.innerHTML = '';
        const iconString = isStarting ? pauseIcon : playIcon;
        content.append(new CircleProgressBarButton(iconString, getAnimationDuration(), isStarting));

        // When live mode was ended by other sources as the user, make sure that a toast message is displayed.
        if (currentState?.state === 'running' && statusChange?.state === 'stopped' && !statusChange?.silent) {
          showToastMessage(i18n('liveMode.paused.info'));
        }

        // Manage the timer that keeps updating the time since last refresh
        if (isStarting) {
          this.#startUpdatingLastRefreshTime();
        } else {
          this.#stopUpdatingLastRefreshTime();
          this.#refreshUpdateTime();
        }
      }),
      observeStore(getStore(), getLiveModeLastRefreshTime, (time) => {
        if (isLiveModeRunning(getStore().getState())) {
          this.#refreshUpdateTime(time);
        }
      }),
    ];
  }

  disconnectedCallback() {
    this.#stopUpdatingLastRefreshTime();
    this.#unsubscribeFromStore = unsubscribeStore(this.#unsubscribeFromStore);
  }

  #startUpdatingLastRefreshTime() {
    this.#updateTimer = window.setInterval(() => {
      this.#refreshUpdateTime(getLiveModeLastRefreshTime(getStore().getState()));
    }, 500);
  }

  #stopUpdatingLastRefreshTime() {
    if (this.#updateTimer) {
      window.clearInterval(this.#updateTimer);
      this.#updateTimer = undefined;
    }
  }

  #refreshUpdateTime(lastUpdateTime?: number) {
    let timestamp = '-';
    if (lastUpdateTime) {
      const msSinceUpdate = new Date().getTime() - lastUpdateTime;
      if (msSinceUpdate < 60_000) {
        timestamp = formatRelativeTime(Math.round(-msSinceUpdate / 1000), 'second');
      } else {
        timestamp = formatDateTime(new Date(lastUpdateTime));
        // Once absolute time is displayed, no further updates are required
        this.#stopUpdatingLastRefreshTime();
      }
    }
    this.#lastUpdateTime.textContent = `${i18n('lastUpdateTime.label')}: ${timestamp}`;
  }
}

function getAnimationDuration(): number {
  const activeWorksheet = getActiveWorksheet(getStore().getState());
  const key = WorksheetSettingsKey.toPersistenceKey('PERIODICAL_QUERY_EXECUTION_INTERVAL');
  return getSetting<number>(activeWorksheet, key, 10000);
}

customElements.define(COMPONENT_NAME, LiveModeAction);
